(() => {
  const routes = window.theme.routes;

  /* ---------- Helpers ---------- */
  const escapeHtml = (str) =>
    String(str ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  function formatMoney(cents, trimZeros = false) {
    const format = window.theme.moneyFormat || '${{amount}}';
    const value = (cents / 100);
    const withDelims = (n, decimals, thousands, decimal) => {
      const [whole, frac] = n.toFixed(decimals).split('.');
      const w = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? w + decimal + frac : w;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      let out;
      switch (key) {
        case 'amount_no_decimals': out = withDelims(value, 0, ',', '.'); break;
        case 'amount_with_comma_separator': out = withDelims(value, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': out = withDelims(value, 0, '.', ','); break;
        case 'amount_with_apostrophe_separator': out = withDelims(value, 2, "'", '.'); break;
        default: out = withDelims(value, 2, ',', '.');
      }
      return trimZeros ? out.replace(/[.,]00$/, '') : out;
    }).replace(/<[^>]*>/g, '');
  }

  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
    return data;
  }

  /* ---------- Cart drawer ---------- */
  const drawer = document.querySelector('[data-cart-drawer]');
  const itemsEl = drawer?.querySelector('[data-cart-items]');
  const footEl = drawer?.querySelector('[data-cart-foot]');
  const subtotalEl = drawer?.querySelector('[data-cart-subtotal]');
  let lastFocus = null;

  function updateCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }

  function renderCart(cart) {
    updateCount(cart.item_count);
    if (!drawer) return;
    subtotalEl.textContent = formatMoney(cart.total_price);
    footEl.hidden = cart.item_count === 0;

    if (cart.item_count === 0) {
      itemsEl.innerHTML = `<div class="drawer__empty"><p>Your bag is empty.</p><button type="button" class="btn" data-cart-close>Continue shopping</button></div>`;
      return;
    }

    itemsEl.innerHTML = cart.items.map((item) => {
      const img = item.image
        ? `<img src="${escapeHtml(item.image)}${item.image.includes('?') ? '&' : '?'}width=160" alt="${escapeHtml(item.product_title)}" width="76" height="76" loading="lazy">`
        : '';
      const variant = item.variant_title && !item.product_has_only_default_variant
        ? `<p class="muted">${escapeHtml(item.variant_title)}</p>` : '';
      const brand = item.vendor ? `<p class="card__brand">${escapeHtml(item.vendor)}</p>` : '';
      return `
        <div class="line" data-key="${escapeHtml(item.key)}">
          <a href="${escapeHtml(item.url)}" class="line__img">${img}</a>
          <div class="line__info">
            ${brand}
            <a href="${escapeHtml(item.url)}" class="line__title">${escapeHtml(item.product_title)}</a>
            ${variant}
            <div class="stepper">
              <button type="button" data-qty="${item.quantity - 1}" aria-label="Decrease quantity">
                <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 12h14"/></svg>
              </button>
              <span>${item.quantity}</span>
              <button type="button" data-qty="${item.quantity + 1}" aria-label="Increase quantity">
                <svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>
              </button>
            </div>
            <button type="button" class="line__remove" data-qty="0">Remove</button>
          </div>
          <div class="line__price">${formatMoney(item.final_line_price)}</div>
        </div>`;
    }).join('');
  }

  async function refreshCart() {
    const res = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } });
    const cart = await res.json();
    renderCart(cart);
    return cart;
  }

  function openDrawer() {
    if (!drawer) { window.location.href = routes.cart; return; }
    lastFocus = document.activeElement;
    drawer.hidden = false;
    document.body.classList.add('drawer-open');
    drawer.querySelector('.drawer__head [data-cart-close]')?.focus();
  }

  function closeDrawer() {
    if (!drawer || drawer.hidden) return;
    drawer.hidden = true;
    document.body.classList.remove('drawer-open');
    lastFocus?.focus();
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-open]') && drawer && !document.body.classList.contains('template-cart')) {
      e.preventDefault();
      refreshCart();
      openDrawer();
    }
    if (e.target.closest('[data-cart-close]')) closeDrawer();
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawer(); });

  itemsEl?.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-qty]');
    if (!btn) return;
    const line = btn.closest('[data-key]');
    line.style.opacity = '.5';
    try {
      const cart = await postJSON(`${routes.cartChange}.js`, { id: line.dataset.key, quantity: Number(btn.dataset.qty) });
      renderCart(cart);
    } catch (err) {
      line.style.opacity = '';
      alert(err.message);
    }
  });

  /* ---------- Ajax add to cart (any form with data-ajax-add) ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[data-ajax-add]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const errorEl = form.querySelector('[data-form-error]');
    const fd = new FormData(form);
    btn?.classList.add('is-loading');
    if (errorEl) errorEl.hidden = true;
    try {
      await postJSON(`${routes.cartAdd}.js`, { items: [{ id: Number(fd.get('id')), quantity: Number(fd.get('quantity') || 1) }] });
      await refreshCart();
      openDrawer();
    } catch (err) {
      if (errorEl) { errorEl.textContent = err.message; errorEl.hidden = false; }
    } finally {
      btn?.classList.remove('is-loading');
    }
  });

  /* ---------- Mobile menu ---------- */
  const toggle = document.querySelector('[data-menu-toggle]');
  const mobileNav = document.getElementById('MobileNav');
  toggle?.addEventListener('click', () => {
    const open = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!open));
    toggle.setAttribute('aria-label', open ? 'Open menu' : 'Close menu');
    mobileNav.hidden = open;
  });

  /* ---------- Collection sort ---------- */
  document.querySelector('[data-sort]')?.addEventListener('change', (e) => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', e.target.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  /* ---------- Product page: gallery + variant picker ---------- */
  document.querySelectorAll('[data-product]').forEach((root) => {
    const mainImg = root.querySelector('[data-gallery-img]');
    const thumbs = root.querySelectorAll('[data-thumb]');

    function showMedia(src, id) {
      if (!mainImg || !src) return;
      mainImg.removeAttribute('srcset');
      mainImg.removeAttribute('sizes');
      mainImg.src = src;
      thumbs.forEach((t) => t.setAttribute('aria-pressed', String(t.dataset.mediaId === String(id))));
    }
    thumbs.forEach((t) => t.addEventListener('click', () => showMedia(t.dataset.thumb, t.dataset.mediaId)));

    const variantsEl = root.querySelector('[data-variants]');
    if (!variantsEl) return;
    const variants = JSON.parse(variantsEl.textContent);
    const selects = root.querySelectorAll('[data-option]');
    const input = root.querySelector('[data-variant-input]');
    const priceEl = root.querySelector('[data-price]');
    const compareEl = root.querySelector('[data-compare]');
    const addBtn = root.querySelector('[data-add-btn]');
    const addLabel = root.querySelector('[data-add-label]');

    function update() {
      const chosen = Array.from(selects).map((s) => s.value);
      const v = variants.find((x) => x.options.every((o, i) => o === chosen[i]));
      if (!v) {
        if (addBtn) { addBtn.disabled = true; addLabel.textContent = 'Unavailable'; }
        return;
      }
      input.value = v.id;
      if (addBtn) {
        priceEl.textContent = formatMoney(v.price);
        compareEl.hidden = !(v.compare_at_price > v.price);
        if (v.compare_at_price) compareEl.textContent = formatMoney(v.compare_at_price);
        addBtn.disabled = !v.available;
        addLabel.textContent = v.available ? 'Add to bag' : 'Sold';
      }
      if (v.featured_media) {
        const thumb = root.querySelector(`[data-media-id="${v.featured_media.id}"]`);
        showMedia(thumb?.dataset.thumb || v.featured_media.preview_image?.src, v.featured_media.id);
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url);
    }
    selects.forEach((s) => s.addEventListener('change', update));
  });

  // Keep the header count right when coming back via the browser's back button
  window.addEventListener('pageshow', (e) => { if (e.persisted) refreshCart(); });
})();
